import React, { useEffect, useRef, useState, useCallback } from "react";
import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { ScreenshotWithComments } from "./ScreenshotWithComments";
import { profilePictures } from "@/lib/data";

interface ElementComment {
  url: string;
  selector: string;
  xpath?: string;
  offsetX: number;
  offsetY: number;
  fallbackX?: number;
  fallbackY?: number;
  text: string;
  commentNumber: number;
  createdAt: Date;
  updatedAt: Date;
  textContent?: string;
  directText?: string;
  attributes?: Record<string, string>;
  styles?: {
    color?: string;
    backgroundColor?: string;
    fontSize?: string;
    fontFamily?: string;
  };
  innerHTML?: string;
  outerHTML?: string;
  elementWidth?: number;
  elementHeight?: number;
  screenshot?: string;
  screenshotX?: number;
  screenshotY?: number;
  screenshotXPercent?: number;
  screenshotYPercent?: number;
  username?: string;
  images?: string[];
  profilePicture?: number;
}

type PropType = {
  slides: { mediaType: string; url: string; filename?: string; elementComment?: ElementComment; elementComments?: ElementComment[] }[];
  options?: EmblaOptionsType;
  onClose?: (currentTime?: number, currentIndex?: number) => void;
  onIndexChange?: (index: number) => void;
  currentIndex?: number;
  startTime?: number;
  autoPlay?: boolean;
  mediaType: "image" | "video" | "dom-replay" | "comments" | undefined;
  report?: any;
  onOpenNewImages?: (images: string[], startIndex: number) => void;
};

type VideoState = {
  duration: number;
  currentTime: number;
  isPlaying: boolean;
};

const MediaSliderFull: React.FC<PropType> = (props) => {
  const {
    slides,
    options,
    onClose,
    onIndexChange,
    currentIndex: currentIndexProp,
    startTime = 0,
    autoPlay = false,
    mediaType,
    report,
    onOpenNewImages,
  } = props;

  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const [currentIndex, setCurrentIndex] = useState(currentIndexProp ?? 0);

  const videoRefs = useRef<{ [key: number]: HTMLVideoElement }>({});
  const [videoStates, setVideoStates] = useState<{ [key: number]: VideoState }>(
    {}
  );
  const [isDraggingPlayhead, setIsDraggingPlayhead] = useState<{
    [key: number]: boolean;
  }>({});
  const [isVideoSeeking, setIsVideoSeeking] = useState<{
    [key: number]: boolean;
  }>({});
  const animationFrameRefs = useRef<{ [key: number]: number }>({});
  const startTimeApplied = useRef<{ [key: number]: boolean }>({});

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const newIndex = emblaApi.selectedScrollSnap();
    setCurrentIndex(newIndex);
    onIndexChange?.(newIndex);
  }, [emblaApi, onIndexChange]);

  const hasScrolledToInitial = useRef(false);

  useEffect(() => {
    if (
      emblaApi &&
      currentIndexProp !== undefined &&
      !hasScrolledToInitial.current
    ) {
      emblaApi.scrollTo(currentIndexProp, true);
      hasScrolledToInitial.current = true;
    }
  }, [emblaApi, currentIndexProp]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  const handlePrevClick = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const handleNextClick = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const handleImageClick = useCallback((images: string[], startIndex: number) => {
    if (onOpenNewImages) {
      onOpenNewImages(images, startIndex);
    }
  }, [onOpenNewImages]);

  const prevBtnDisabled = !emblaApi || currentIndex === 0;
  const nextBtnDisabled = !emblaApi || currentIndex === slides.length - 1;

  useEffect(() => {
    function handleKeyDown(event: any) {
      event.stopPropagation();
      if (event.key === "Escape") {
        handleClose();
      } else if (event.key === "ArrowRight" && !nextBtnDisabled) {
        handleNextClick();
      } else if (event.key === "ArrowLeft" && !prevBtnDisabled) {
        handlePrevClick();
      }
    }
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [handleNextClick, handlePrevClick, prevBtnDisabled, nextBtnDisabled]);

  const handleThumbnailClick = (index: number) => {
    if (emblaApi) {
      emblaApi.scrollTo(index);
    }
  };

  const handleClose = () => {
    const video = videoRefs.current[currentIndex];
    const currentTime = video?.currentTime || 0;
    onClose?.(currentTime, currentIndex);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking the backdrop itself, not any child elements
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const startAnimationLoop = useCallback((index: number) => {
    const updateTime = () => {
      const video = videoRefs.current[index];
      if (video && !video.paused && !video.ended) {
        setVideoStates((prev) => ({
          ...prev,
          [index]: {
            ...prev[index],
            currentTime: video.currentTime,
            duration:
              video.duration && isFinite(video.duration)
                ? video.duration
                : prev[index]?.duration || 0,
          },
        }));
        animationFrameRefs.current[index] = requestAnimationFrame(updateTime);
      }
    };
    animationFrameRefs.current[index] = requestAnimationFrame(updateTime);
  }, []);

  const stopAnimationLoop = useCallback((index: number) => {
    if (animationFrameRefs.current[index]) {
      cancelAnimationFrame(animationFrameRefs.current[index]);
      delete animationFrameRefs.current[index];
    }
  }, []);

  useEffect(() => {
    return () => {
      Object.values(animationFrameRefs.current).forEach((frameId) => {
        cancelAnimationFrame(frameId);
      });
    };
  }, []);

  const setVideoRef = (index: number) => {
    return (el: HTMLVideoElement | null) => {
      if (el && !videoRefs.current[index]) {
        videoRefs.current[index] = el;

        const initialTime =
          index === currentIndexProp && startTime > 0 ? startTime : 0;

        setVideoStates((prev) => ({
          ...prev,
          [index]: { duration: 0, currentTime: initialTime, isPlaying: false },
        }));

        const applyStartTime = () => {
          if (
            index === currentIndexProp &&
            startTime > 0 &&
            !startTimeApplied.current[index]
          ) {
            setIsVideoSeeking((prev) => ({ ...prev, [index]: true }));
            el.currentTime = startTime;
            startTimeApplied.current[index] = true;
            setVideoStates((prev) => ({
              ...prev,
              [index]: { ...prev[index], currentTime: startTime },
            }));
            if (autoPlay) {
              el.play().catch((err) =>
                console.error("Error auto-playing video:", err)
              );
            }
          } else if (
            index === currentIndexProp &&
            startTime === 0 &&
            autoPlay &&
            !startTimeApplied.current[index]
          ) {
            startTimeApplied.current[index] = true;
            el.play().catch((err) =>
              console.error("Error auto-playing video:", err)
            );
          }
        };

        const handleSeeked = () => {
          setIsVideoSeeking((prev) => ({ ...prev, [index]: false }));
        };

        const handleLoadedMetadata = () => {
          if (el.duration && isFinite(el.duration)) {
            setVideoStates((prev) => ({
              ...prev,
              [index]: { ...prev[index], duration: el.duration },
            }));
            applyStartTime();
          }
        };

        const handleLoadedData = () => {
          applyStartTime();
        };

        const handleCanPlay = () => {
          if (el.duration && isFinite(el.duration)) {
            setVideoStates((prev) => ({
              ...prev,
              [index]: { ...prev[index], duration: el.duration },
            }));
          }
          applyStartTime();
        };

        const handleDurationChange = () => {
          if (el.duration && isFinite(el.duration)) {
            setVideoStates((prev) => ({
              ...prev,
              [index]: { ...prev[index], duration: el.duration },
            }));
          }
        };

        el.addEventListener("loadedmetadata", handleLoadedMetadata);
        el.addEventListener("loadeddata", handleLoadedData);
        el.addEventListener("durationchange", handleDurationChange);
        el.addEventListener("canplay", handleCanPlay);
        el.addEventListener("seeked", handleSeeked);

        // Try to apply immediately if possible
        if (el.readyState >= 1) {
          applyStartTime();
        }

        setTimeout(() => {
          if (!isFinite(el.duration)) {
            el.currentTime = 1e101;
            const seeked = () => {
              if (el.currentTime > 0 && isFinite(el.duration)) {
                setVideoStates((prev) => ({
                  ...prev,
                  [index]: { ...prev[index], duration: el.duration },
                }));
                el.currentTime =
                  startTime > 0 && index === currentIndexProp ? startTime : 0;
                startTimeApplied.current[index] = true;
                el.removeEventListener("timeupdate", seeked);
              }
            };
            el.addEventListener("timeupdate", seeked);
          }
        }, 500);
      }
    };
  };

  const handleVideoLoadedMetadata = (index: number) => {
    const video = videoRefs.current[index];
    if (
      video &&
      video.duration &&
      !isNaN(video.duration) &&
      isFinite(video.duration)
    ) {
      setVideoStates((prev) => ({
        ...prev,
        [index]: { ...prev[index], duration: video.duration },
      }));
    }
  };

  const handleDurationChange = (index: number) => {
    const video = videoRefs.current[index];
    if (
      video &&
      video.duration &&
      !isNaN(video.duration) &&
      isFinite(video.duration)
    ) {
      setVideoStates((prev) => ({
        ...prev,
        [index]: { ...prev[index], duration: video.duration },
      }));
    }
  };

  const handleVideoTimeUpdate = (index: number) => {
    const video = videoRefs.current[index];
    if (video && !isDraggingPlayhead[index]) {
      setVideoStates((prev) => ({
        ...prev,
        [index]: { ...prev[index], currentTime: video.currentTime },
      }));
    }
  };

  const handleVideoPlay = (index: number) => {
    setVideoStates((prev) => ({
      ...prev,
      [index]: { ...prev[index], isPlaying: true },
    }));
    startAnimationLoop(index);
  };

  const handleVideoPause = (index: number) => {
    setVideoStates((prev) => ({
      ...prev,
      [index]: { ...prev[index], isPlaying: false },
    }));
    stopAnimationLoop(index);
  };

  const handleVideoEnded = (index: number) => {
    setVideoStates((prev) => ({
      ...prev,
      [index]: { ...prev[index], isPlaying: false },
    }));
    stopAnimationLoop(index);
  };

  const handleTimelineClick = (
    e: React.MouseEvent<HTMLDivElement>,
    index: number
  ) => {
    const video = videoRefs.current[index];
    if (!video) return;

    const timeline = e.currentTarget;
    const rect = timeline.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * video.duration;

    if (isFinite(newTime)) {
      video.currentTime = newTime;
      setVideoStates((prev) => ({
        ...prev,
        [index]: { ...prev[index], currentTime: newTime },
      }));
    }
  };

  const handlePlayheadMouseDown = (
    e: React.MouseEvent,
    index: number
  ): void => {
    e.preventDefault();
    e.stopPropagation();

    const video = videoRefs.current[index];
    if (!video) return;

    const wasPlaying = videoStates[index]?.isPlaying;
    if (wasPlaying) {
      video.pause();
    }

    setIsDraggingPlayhead((prev) => ({ ...prev, [index]: true }));

    const handleMouseMove = (moveEvent: MouseEvent): void => {
      const timeline = document.querySelector(
        `[data-timeline-index="${index}"]`
      ) as HTMLElement;
      if (!timeline) return;

      const rect = timeline.getBoundingClientRect();
      const x = Math.max(
        0,
        Math.min(moveEvent.clientX - rect.left, rect.width)
      );
      const percentage = x / rect.width;
      const newTime = percentage * video.duration;

      if (isFinite(newTime)) {
        video.currentTime = newTime;
        setVideoStates((prev) => ({
          ...prev,
          [index]: { ...prev[index], currentTime: newTime },
        }));
      }
    };

    const handleMouseUp = (): void => {
      setIsDraggingPlayhead((prev) => ({ ...prev, [index]: false }));
      if (wasPlaying) {
        video.play().catch(console.error);
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const togglePlayPause = (index: number) => {
    const video = videoRefs.current[index];
    if (video) {
      if (video.paused) {
        video
          .play()
          .catch((err) => console.error(`Error playing video ${index}:`, err));
      } else {
        video.pause();
      }
    }
  };

  const formatTime = (seconds: number): string => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const getVideoState = (index: number): VideoState => {
    return (
      videoStates[index] || { duration: 0, currentTime: 0, isPlaying: false }
    );
  };

  const getProgressPercentage = (index: number): number => {
    const state = getVideoState(index);
    if (!state.duration || !isFinite(state.duration)) return 0;
    return Math.min(100, (state.currentTime / state.duration) * 100);
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          maxWidth: "100%",
          margin: "auto",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          zIndex: 10000000000000,
          background: "rgba(15, 15, 15, 0.8)",
          backdropFilter: "blur(7px)",
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
        }}
        onClick={handleBackdropClick}
      >
        <div style={{ overflow: "hidden" }} ref={emblaRef}>
          <div
            style={{
              display: "flex",
              touchAction: "pan-y pinch-zoom",
              marginLeft: "calc(var(--slide-spacing) * -1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {slides.map((slide, index) => {
              const videoState = getVideoState(index);
              const progress = getProgressPercentage(index);

              return (
                <div
                  key={index}
                  style={{
                    height: "100vh",
                    width: "100vw",
                    transform: "translate3d(0, 0, 0)",
                    flex: "0 0 100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "0",
                    paddingLeft: "var(--slide-spacing)",
                  }}
                >
                  <div
                    style={{
                      width: "100%",
                      maxWidth: "90%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "1.5rem",
                    }}
                  >
                    {slide.mediaType === "image" ? (
                      <ScreenshotWithComments
                        imageUrl={slide.url}
                        onImageClick={handleImageClick}
                        comments={(() => {
                          // For "comments" mediaType, use elementComments (array) or elementComment from the slide
                          if (mediaType === "comments") {
                            // Handle multiple comments per slide (new format)
                            if (slide.elementComments && slide.elementComments.length > 0) {
                              return slide.elementComments
                                .map((ec, commentIndex) => {
                                  // Prefer percentage values if available (more accurate)
                                  if (ec.screenshotXPercent != null && ec.screenshotYPercent != null) {
                                    return {
                                      id: ec.commentNumber || commentIndex + 1,
                                      percentX: ec.screenshotXPercent,
                                      percentY: ec.screenshotYPercent,
                                      text: ec.text || "",
                                      username: ec.username,
                                      images: ec.images,
                                      profilePicture: ec.profilePicture != null ? profilePictures[ec.profilePicture - 1] : undefined,
                                    };
                                  }
                                  // Fall back to pixel coordinates if percentage not available
                                  if (ec.screenshotX != null && ec.screenshotY != null) {
                                    return {
                                      id: ec.commentNumber || commentIndex + 1,
                                      percentX: 0, // Will be overridden by pixelX
                                      percentY: 0, // Will be overridden by pixelY
                                      pixelX: ec.screenshotX,
                                      pixelY: ec.screenshotY,
                                      text: ec.text || "",
                                      username: ec.username,
                                      images: ec.images,
                                      profilePicture: ec.profilePicture != null ? profilePictures[ec.profilePicture - 1] : undefined,
                                    };
                                  }
                                  return null;
                                })
                                .filter(Boolean);
                            }
                            // Handle single comment per slide (legacy format)
                            if (slide.elementComment) {
                              const ec = slide.elementComment;
                              // Prefer percentage values if available (more accurate)
                              if (ec.screenshotXPercent != null && ec.screenshotYPercent != null) {
                                return [{
                                  id: ec.commentNumber || index + 1,
                                  percentX: ec.screenshotXPercent,
                                  percentY: ec.screenshotYPercent,
                                  text: ec.text || "",
                                  username: ec.username,
                                  images: ec.images,
                                  profilePicture: ec.profilePicture != null ? profilePictures[ec.profilePicture - 1] : undefined,
                                }];
                              }
                              // Fall back to pixel coordinates if percentage not available
                              if (ec.screenshotX != null && ec.screenshotY != null) {
                                return [{
                                  id: ec.commentNumber || index + 1,
                                  percentX: 0, // Will be overridden by pixelX
                                  percentY: 0, // Will be overridden by pixelY
                                  pixelX: ec.screenshotX,
                                  pixelY: ec.screenshotY,
                                  text: ec.text || "",
                                  username: ec.username,
                                  images: ec.images,
                                  profilePicture: ec.profilePicture != null ? profilePictures[ec.profilePicture - 1] : undefined,
                                }];
                              }
                            }
                            return [];
                          }
                          // For "image" mediaType, use screenshotComments on first slide only
                          if (mediaType === "image" && index === 0) {
                            return (report?.screenshotComments || []).map((sc: any) => ({
                              ...sc,
                              profilePicture: sc.profilePicture != null ? profilePictures[sc.profilePicture - 1] : undefined,
                            }));
                          }
                          return [];
                        })()}
                        alt={slide.filename || ""}
                        className=""
                        style={{
                          objectFit: "contain",
                          maxHeight: "70vh",
                          maxWidth: "100%",
                        }}
                      />
                    ) : (
                      <>
                        <div style={{ position: "relative" }}>
                          <video
                            ref={setVideoRef(index)}
                            src={slide.url}
                            controls={false}
                            style={{
                              objectFit: "contain",
                              maxHeight: "70vh",
                              maxWidth: "100%",
                              borderRadius: "8px",
                              cursor: "pointer",
                            }}
                            autoPlay={false}
                            preload="metadata"
                            muted={false}
                            onLoadedMetadata={() =>
                              handleVideoLoadedMetadata(index)
                            }
                            onDurationChange={() => handleDurationChange(index)}
                            onTimeUpdate={() => handleVideoTimeUpdate(index)}
                            onPlay={() => handleVideoPlay(index)}
                            onPause={() => handleVideoPause(index)}
                            onEnded={() => handleVideoEnded(index)}
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePlayPause(index);
                            }}
                          />
                          {isVideoSeeking[index] && (
                            <div
                              style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                background: "rgba(0, 0, 0, 0.7)",
                                borderRadius: "8px",
                                padding: "12px 20px",
                                color: "white",
                                fontSize: "14px",
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <div
                                style={{
                                  width: "16px",
                                  height: "16px",
                                  border: "2px solid white",
                                  borderTopColor: "transparent",
                                  borderRadius: "50%",
                                  animation: "spin 0.8s linear infinite",
                                }}
                              />
                              Seeking...
                            </div>
                          )}
                        </div>
                        {slide.mediaType !== "image" && (
                          <div
                            style={{
                              width: "100%",
                              maxWidth: "1200px",
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePlayPause(index);
                            }}
                            style={{
                              background: "rgba(255, 255, 255, 0.08)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: "48px",
                              height: "48px",
                              borderRadius: "14px",
                              transition: "all 0.2s",
                              cursor: "pointer",
                              flexShrink: 0,
                              border: "none",
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background =
                                "rgba(255, 255, 255, 0.16)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background =
                                "rgba(255, 255, 255, 0.08)")
                            }
                          >
                            {videoState.isPlaying ? (
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"
                                  fill="white"
                                />
                              </svg>
                            ) : (
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path d="M8 5v14l11-7z" fill="white" />
                              </svg>
                            )}
                          </button>

                          <div
                            style={{
                              flex: 1,
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              height: "48px",
                            }}
                          >
                            <div
                              data-timeline-index={index}
                              style={{
                                width: "100%",
                                height: "48px",
                                borderRadius: "10px",
                                position: "relative",
                                cursor: "pointer",
                                background: "rgba(255, 255, 255, 0.08)",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTimelineClick(e, index);
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  height: "100%",
                                  borderRadius: "10px",
                                  background: "white",
                                  pointerEvents: "none",
                                  width: `${progress}%`,
                                }}
                              />

                              <div
                                style={{
                                  position: "absolute",
                                  top: "-32px",
                                  height: "24px",
                                  borderRadius: "8px",
                                  padding: "0 8px",
                                  backdropFilter: "blur(10px)",
                                  background: "rgba(51, 51, 51, 0.64)",
                                  border: "1px solid rgba(156, 163, 175, 0.64)",
                                  color: "white",
                                  fontSize: "14px",
                                  cursor: "grab",
                                  transition: "transform 0.1s",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  whiteSpace: "nowrap",
                                  zIndex: 5,
                                  left: `${progress}%`,
                                  transform: "translateX(-50%)",
                                }}
                                onMouseDown={(e) =>
                                  handlePlayheadMouseDown(e, index)
                                }
                              >
                                {formatTime(videoState.currentTime)}
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "28px",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: "1px",
                                    height: "56px",
                                    background: "white",
                                    borderRadius: "10px",
                                    pointerEvents: "none",
                                    boxShadow:
                                      "0px 4px 4px 0px rgba(0, 0, 0, 0.2)",
                                  }}
                                />
                              </div>
                            </div>
                          </div>

                          <div
                            style={{
                              color: "rgba(255, 255, 255, 0.8)",
                              fontSize: "15px",
                              padding: "0 20px",
                              lineHeight: "20px",
                              background: "rgba(255, 255, 255, 0.08)",
                              borderRadius: "14px",
                              fontWeight: 500,
                              height: "48px",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {formatTime(videoState.currentTime)} /{" "}
                            {formatTime(videoState.duration)}
                          </div>
                        </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {(mediaType === "image" || mediaType === "comments") && (
          <div
            style={{
              position: "fixed",
              left: "50%",
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              fontSize: 14,
              fontFamily: "Onest",
              fontWeight: 600,
              transform: "translateX(-50%)",
              bottom: 25,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 15 }}>
                {slides.map((slide, index) => (
                  <div
                    key={index}
                    onClick={() => handleThumbnailClick(index)}
                    style={{ cursor: "pointer" }}
                  >
                    {slide.mediaType === "image" ? (
                      <img
                        src={slide.url}
                        style={{
                          borderRadius: 11,
                          width: 36,
                          height: 36,
                          objectFit: "cover",
                          opacity: currentIndex === index ? 1 : 0.6,
                        }}
                        className={`toolbar__embla__slider__thumbnail ${
                          index === currentIndex ? "active" : ""
                        }`}
                      />
                    ) : (
                      <video
                        src={slide.url}
                        style={{
                          borderRadius: 11,
                          width: 36,
                          height: 36,
                          objectFit: "cover",
                          opacity: currentIndex === index ? 1 : 0.6,
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: 14,
                fontFamily: "Onest",
                fontWeight: 600,
              }}
            >
              <div
                onClick={handlePrevClick}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  cursor: prevBtnDisabled ? "not-allowed" : "pointer",
                  gap: "0.6rem",
                  alignItems: "center",
                  borderRadius: "15px",
                  width: "38px",
                  height: "38px",
                  backgroundColor: "transparent",
                  transition: "all 0.2s",
                  boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                  zIndex: 100,
                  opacity: prevBtnDisabled ? 0.4 : 1,
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18l-6-6 6-6"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <div
                style={{
                  width: 50,
                  display: "flex",
                  justifyContent: "center",
                  color: "white",
                }}
              >
                {currentIndex + 1}/{slides.length}
              </div>

              <div
                onClick={handleNextClick}
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "0.6rem",
                  cursor: nextBtnDisabled ? "not-allowed" : "pointer",
                  alignItems: "center",
                  borderRadius: "15px",
                  width: "38px",
                  height: "38px",
                  backgroundColor: "transparent",
                  transition: "all 0.2s",
                  zIndex: 100,
                  opacity: nextBtnDisabled ? 0.4 : 1,
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 18l6-6-6-6"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
        )}

        {onClose && (
          <div
            onClick={handleClose}
            style={{
              position: "absolute",
              top: 50,
              right: 50,
              width: "38px",
              height: "38px",
              borderRadius: "15px",
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        )}
      </motion.section>
    </>
  );
};

export default MediaSliderFull;
